import { AsyncSubject, Observable, of } from 'rxjs';
import {
  Client,
  ClientAddress,
  ClientContact,
  ClientListItem,
  ClientState,
  ParentOrganization,
} from '../data/models/client.model';

import httpService from './httpService';
import {
  Program,
  ServicePlan,
  ServicePlanList,
  ServicePlanServiceArea,
} from '../data/models/client-program.model';
import { PassengerClientData } from '../data/models/passenger-client-data.model';
import {
  TransportationBenefitModel,
  TransportationBenefitModelState,
} from '../data/models/transportation-benefit-model.model';
import { TransportationBenefitFunction } from '../data/models/transportation-benefit-function.model';
import { TransportationBenefit } from '../data/models/transportation-benefit.model';
import {
  CreatePassengerIngestionServerConfigurationRequest,
  EditablePassengerIngestionServerConfiguration,
  PassengerIngestionServerConfiguration,
} from '../data/models/passenger-ingestion-server-configuration.model';
import { UpdatePassengerIngestionServerConfigurationEffectiveDatesRequest } from '../data/models/update-passenger-ingestion-server-configuration-effective-dates-request.model';
import {
  CreatableNamedLocation,
  EditableNamedLocation,
} from '../data/models/named-location.model';
import {
  CreatableClientLocation,
  DeleteClientLocationRequest,
} from '../data/models/client-location.model';
import { ClientSetting } from '../data/models/client-settings.model';
import { PassengerIngestionExecutionStatus } from '../data/models/passenger-ingestion.model';
import { PagedList } from '../data/models/paged-list.model';
import { ClientPurposeCode } from '../data/models/purpose-code-model.model';
import { ClientAdminSettings } from '../data/models/client-admin-settings.model';

class ClientService {
  private readonly apiBase;
  private readonly apiBaseTransport;

  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_CLIENT_API_URL.trim();
    this.apiBaseTransport =
      __environment__.REACT_APP_TRANSPORT_CLIENT_API_URL.trim();
  }

  public getParentOrganizations(): Observable<ParentOrganization[]> {
    const url = `${this.apiBase}/parentOrganizationSummaryList`;
    return httpService.getJSON(url);
  }

  public assignClientToParentOrganization(
    clientBC: string,
    parentOrganizationBC: string,
  ): Observable<Client> {
    const url = `${this.apiBase}/assignClientToParentOrganization`;
    return httpService.postJSON(url, {
      clientBC,
      parentOrganizationBC,
    });
  }

  public getClientsList(
    search?: string,
    page?: number,
    pageSize?: number,
  ): Observable<PagedList<ClientListItem>> {
    const url = `${this.apiBaseTransport}?search=${search}&page=${page}&pageSize=${pageSize}`;
    return httpService.getJSON(url);
  }

  public getClients(): Observable<Client[]> {
    const url = `${this.apiBase}/getClientList`;
    return httpService.getJSON(url);
  }

  public getClient(id: string): Observable<Client> {
    const url = `${this.apiBase}/getClient?clientBC=${id}`;
    return httpService.getJSON(url);
  }

  public createClient(client: Client): Observable<Client> {
    const url = `${this.apiBase}/createClient`;
    return httpService.postJSON(url, client);
  }

  public updateClient(client: Client): Observable<Client> {
    const url = `${this.apiBase}/updateClient`;
    return httpService.postJSON(url, client);
  }

  public deleteClient(clientBC: string): Observable<void> {
    const url = `${this.apiBase}/deleteClient`;
    return httpService.postJSON(url, { clientBC });
  }

  public updateClientEffectiveDates(
    clientBC: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<Client> {
    const url = `${this.apiBase}/updateClientEffectiveDates`;
    return httpService.postJSON(url, {
      clientBC,
      effectiveStartDate,
      effectiveEndDate,
    });
  }

  public getClientAddresses(
    clientBC: string,
    states?: string[],
  ): Observable<ClientAddress[]> {
    let url = `${this.apiBase}/getClientAddresses?clientBC=${clientBC}`;
    if (states?.length) {
      url += `&states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  private cleanAddress(address: ClientAddress) {
    return {
      clientAddressUUID: address.clientAddressUUID,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      city: address.city,
      stateCode: address.stateCode,
      county: address.county,
      zipCode: address.zipCode,
      addressUseBC: address.addressUseBC,
    };
  }

  public createClientAddress(
    clientBC: string,
    address: ClientAddress,
  ): Observable<ClientAddress> {
    const url = `${this.apiBase}/createClientAddress`;
    return httpService.postJSON(url, {
      clientBC,
      ...this.cleanAddress(address),
    });
  }

  public updateClientAddress(
    clientBC: string,
    address: ClientAddress,
  ): Observable<ClientAddress> {
    const url = `${this.apiBase}/updateClientAddress`;
    return httpService.postJSON(url, {
      clientBC,
      ...this.cleanAddress(address),
    });
  }

  public activateClientAddress(
    clientBC: string,
    clientAddress: ClientAddress,
  ): Observable<ClientAddress> {
    const url = `${this.apiBase}/activateClientAddress`;
    return httpService.postJSON(url, {
      clientBC,
      clientAddressUUID: clientAddress.clientAddressUUID,
    });
  }

  public deactivateClientAddress(
    clientBC: string,
    clientAddress: ClientAddress,
  ): Observable<ClientAddress> {
    const url = `${this.apiBase}/deactivateClientAddress`;
    return httpService.postJSON(url, {
      clientBC,
      clientAddressUUID: clientAddress.clientAddressUUID,
    });
  }

  public deleteClientAddress(
    clientBC: string,
    clientAddressUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deleteClientAddress`;
    return httpService.postJSON(url, { clientBC, clientAddressUUID });
  }

  public getClientContacts(
    clientBC: string,
    states?: string[],
  ): Observable<ClientContact[]> {
    let url = `${this.apiBase}/getClientContacts?clientBC=${clientBC}`;
    if (states?.length) {
      url += `&states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  public createClientContact(
    clientBC: string,
    contact: ClientContact,
  ): Observable<ClientContact> {
    const url = `${this.apiBase}/createClientContact`;
    return httpService.postJSON(url, { clientBC, ...contact });
  }

  public updateClientContact(
    clientBC: string,
    contact: ClientContact,
  ): Observable<ClientContact> {
    const url = `${this.apiBase}/updateClientContact`;
    return httpService.postJSON(url, { clientBC, ...contact });
  }

  public activateClientContact(
    clientBC: string,
    clientContact: ClientContact,
  ): Observable<ClientContact> {
    const url = `${this.apiBase}/activateClientContact`;
    return httpService.postJSON(url, {
      clientBC,
      clientContactUUID: clientContact.clientContactUUID,
    });
  }

  public deactivateClientContact(
    clientBC: string,
    clientContact: ClientContact,
  ): Observable<ClientContact> {
    const url = `${this.apiBase}/deactivateClientContact`;
    return httpService.postJSON(url, {
      clientBC,
      clientContactUUID: clientContact.clientContactUUID,
    });
  }

  public deleteClientContact(
    clientBC: string,
    clientContactUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deleteClientContact`;
    return httpService.postJSON(url, { clientBC, clientContactUUID });
  }

  public createClientPurposeCode(
    purposeCode: ClientPurposeCode,
  ): Observable<ClientPurposeCode> {
    const url = `${this.apiBase}/createPurposeCode`;
    return httpService.postJSON(url, purposeCode);
  }

  public updateClientPurposeCode(
    purposeCode: ClientPurposeCode,
  ): Observable<ClientPurposeCode> {
    const url = `${this.apiBase}/updatePurposeCode`;
    return httpService.postJSON(url, purposeCode);
  }

  public deleteClientPurposeCode(
    clientBC: string,
    purposeCodeUUID: string,
  ): Observable<ClientPurposeCode> {
    const url = `${this.apiBase}/deletePurposeCode`;
    return httpService.postJSON(url, { clientBC, purposeCodeUUID });
  }

  public getPurposeCodes(
    clientBC: string,
    states?: string[],
  ): Observable<{ clientBC: string; purposeCodes: ClientPurposeCode[] }> {
    let url = `${this.apiBase}/getPurposeCodes?clientBC=${clientBC}`;
    if (states?.length) {
      url += `&states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  public getPrograms(
    id: string,
    states?: string[],
  ): Observable<{ clientBC: string; programs: Program[] }> {
    let url = `${this.apiBase}/getPrograms?clientBC=${id}`;
    if (states?.length) {
      url += `&states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  public getServicePlans(
    clientBC: string,
    continuationToken?: string,
    states?: string[],
  ): Observable<ServicePlanList> {
    let url = `${this.apiBase}/getServicePlans?clientBC=${clientBC}`;
    if (states?.length) {
      url += `&states=${states.join(';')}`;
    }
    if (continuationToken) {
      url += `&continuationToken=${encodeURIComponent(continuationToken)}`;
    }
    return httpService.getJSON(url);
  }

  public getManualServicePlans(
    clientBC: string,
    states?: string[],
  ): Observable<ServicePlanList> {
    // TODO: this needs to specify manual only when it is available on the back end
    let url = `${this.apiBase}/getServicePlans?clientBC=${clientBC}`;
    if (states?.length) {
      url += `&states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  public createClientProgram(program: Program): Observable<Program> {
    const url = `${this.apiBase}/createProgram`;
    return httpService.postJSON(url, program);
  }

  public updateClientProgram(program: Program): Observable<Program> {
    const url = `${this.apiBase}/updateProgram`;
    return httpService.postJSON(url, program);
  }

  public updateProgramEffectiveDates(
    clientBC: string,
    programUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<Program> {
    const url = `${this.apiBase}/updateProgramEffectiveDates`;
    return httpService.postJSON(url, {
      clientBC,
      programUUID,
      effectiveStartDate,
      effectiveEndDate,
    });
  }

  public deleteClientProgram(
    clientBC: string,
    programUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deleteProgram`;
    return httpService.postJSON(url, { clientBC, programUUID });
  }

  public deleteClientProgramSetting(
    programSettingUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deleteProgramSetting`;
    return httpService.postJSON(url, { programSettingUUID });
  }

  public createClientServicePlan(
    servicePlan: ServicePlan,
  ): Observable<ServicePlan> {
    const url = `${this.apiBase}/createServicePlan`;
    return httpService.postJSON(url, servicePlan);
  }

  public reassignClientServicePlan(
    clientBC: string,
    servicePlanUUID: string,
    programUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/reassignServicePlanToProgram`;
    return httpService.postJSON(url, {
      clientBC,
      programUUID,
      servicePlanUUID,
    });
  }

  public unassignClientServicePlan(
    clientBC: string,
    servicePlanUUID: string,
    programUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/unassignServicePlanFromProgram`;
    return httpService.postJSON(url, {
      clientBC,
      programUUID,
      servicePlanUUID,
    });
  }

  public updateServicePlanEffectiveDates(
    clientBC: string,
    servicePlanUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<ServicePlan> {
    const url = `${this.apiBase}/updateServicePlanEffectiveDates`;
    return httpService.postJSON(url, {
      clientBC,
      servicePlanUUID,
      effectiveStartDate,
      effectiveEndDate,
    });
  }

  public deleteServicePlan(
    clientBC: string,
    servicePlanUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deleteServicePlan`;
    return httpService.postJSON(url, { clientBC, servicePlanUUID });
  }

  public assignClientPurposeCode(
    clientBC: string,
    purposeCodeUUID: string,
    servicePlanUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/assignPurposeCodeToServicePlan`;
    return httpService.postJSON(url, {
      clientBC,
      servicePlanUUID,
      purposeCodeUUID,
    });
  }

  public unassignClientPurposeCode(
    clientBC: string,
    purposeCodeUUID: string,
    servicePlanUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/unassignPurposeCodeFromServicePlan`;
    return httpService.postJSON(url, {
      clientBC,
      servicePlanUUID,
      purposeCodeUUID,
    });
  }

  public assignClientServiceArea(
    clientBC: string,
    serviceAreaUUID: string,
    servicePlanUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/assignServiceAreaToServicePlan`;
    return httpService.postJSON(url, {
      clientBC,
      servicePlanUUID,
      serviceAreaUUID,
    });
  }

  public unassignClientServiceArea(
    clientBC: string,
    serviceAreaUUID: string,
    servicePlanUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/unassignServiceAreaFromServicePlan`;
    return httpService.postJSON(url, {
      clientBC,
      servicePlanUUID,
      serviceAreaUUID,
    });
  }

  public assignTransportationBenefit(
    clientBC: string,
    transportationBenefitUUID: string,
    servicePlanUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/assignTransportationBenefitToServicePlan`;
    return httpService.postJSON(url, {
      clientBC,
      servicePlanUUID,
      transportationBenefitUUID,
    });
  }

  public unassignTransportationBenefit(
    clientBC: string,
    transportationBenefitUUID: string,
    servicePlanUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/unassignTransportationBenefitFromServicePlan`;
    return httpService.postJSON(url, {
      clientBC,
      servicePlanUUID,
      transportationBenefitUUID,
    });
  }

  public updateClientServicePlan(
    servicePlan: ServicePlan,
  ): Observable<ServicePlan> {
    const url = `${this.apiBase}/updateServicePlan`;
    return httpService.postJSON(url, servicePlan);
  }

  public activatePurposeCode(
    clientBC: string,
    purposeCode: ClientPurposeCode,
  ): Observable<ClientPurposeCode> {
    const url = `${this.apiBase}/activatePurposeCode`;
    return httpService.postJSON(url, {
      clientBC,
      purposeCodeUUID: purposeCode.purposeCodeUUID,
    });
  }

  public deactivatePurposeCode(
    clientBC: string,
    purposeCode: ClientPurposeCode,
  ): Observable<ClientPurposeCode> {
    const url = `${this.apiBase}/deactivatePurposeCode`;
    return httpService.postJSON(url, {
      clientBC,
      purposeCodeUUID: purposeCode.purposeCodeUUID,
    });
  }

  public deletePurposeCode(
    clientBC: string,
    purposeCodeUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deletePurposeCode`;
    return httpService.postJSON(url, {
      clientBC,
      purposeCodeUUID,
    });
  }

  public getServiceAreas(
    clientBC: string,
    states?: string[],
  ): Observable<ServicePlanServiceArea[]> {
    let url = `${this.apiBase}/getServiceAreas?clientBC=${clientBC}`;
    if (states?.length) {
      url += `&states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  public createServiceArea(
    serviceArea: ServicePlanServiceArea,
  ): Observable<ServicePlanServiceArea> {
    const url = `${this.apiBase}/createServiceArea`;
    return httpService.postJSON(url, serviceArea);
  }

  public updateServiceArea(
    serviceArea: ServicePlanServiceArea,
  ): Observable<ServicePlanServiceArea> {
    const url = `${this.apiBase}/updateServiceArea`;
    return httpService.postJSON(url, serviceArea);
  }

  public activateServiceArea(
    clientBC: string,
    serviceArea: ServicePlanServiceArea,
  ): Observable<ServicePlanServiceArea> {
    const url = `${this.apiBase}/activateServiceArea`;
    return httpService.postJSON(url, {
      clientBC,
      serviceAreaUUID: serviceArea.serviceAreaUUID!,
    });
  }

  public deactivateServiceArea(
    clientBC: string,
    serviceArea: ServicePlanServiceArea,
  ): Observable<ServicePlanServiceArea> {
    const url = `${this.apiBase}/deactivateServiceArea`;
    return httpService.postJSON(url, {
      clientBC,
      serviceAreaUUID: serviceArea.serviceAreaUUID!,
    });
  }

  public deleteServiceArea(
    clientBC: string,
    serviceAreaUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deleteServiceArea`;
    return httpService.postJSON(url, {
      clientBC,
      serviceAreaUUID,
    });
  }

  public activateTransportationBenefit(
    clientBC: string,
    transportationBenefit: TransportationBenefit,
  ): Observable<TransportationBenefit> {
    const url = `${this.apiBase}/activateTransportationBenefit`;
    return httpService.postJSON(url, {
      clientBC,
      transportationBenefit: transportationBenefit.transportationBenefitUUID!,
    });
  }

  public deactivateTransportationBenefit(
    clientBC: string,
    transportationBenefit: TransportationBenefit,
  ): Observable<TransportationBenefit> {
    const url = `${this.apiBase}/deactivateTransportationBenefit`;
    return httpService.postJSON(url, {
      clientBC,
      transportationBenefit: transportationBenefit.transportationBenefitUUID!,
    });
  }

  public deleteTransportationBenefit(
    clientBC: string,
    transportationBenefitUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deleteTransportationBenefit`;
    return httpService.postJSON(url, { clientBC, transportationBenefitUUID });
  }

  public getPassengerClientData(
    clientBC: string,
    servicePlans: { servicePlanUUID: string }[],
  ): Observable<PassengerClientData> {
    const url = `${this.apiBase}/getPassengerClientData`;
    return httpService.postJSON(url, { clientBC, servicePlans });
  }

  public getTransportationBenefitModels(
    states?: TransportationBenefitModelState[],
  ): Observable<TransportationBenefitModel[]> {
    const url = `${this.apiBase}/getTransportationBenefitModels?states=${
      states?.join(',') || 'draft,active,inactive'
    }`;
    return httpService.getJSON(url);
  }

  public createTransportationBenefitModel(
    tbm: TransportationBenefitModel,
  ): Observable<TransportationBenefitModel> {
    const url = `${this.apiBase}/createTransportationBenefitModel`;
    return httpService.postJSON(url, tbm);
  }

  public updateTransportationBenefitModel(
    tbm: TransportationBenefitModel,
  ): Observable<TransportationBenefitModel> {
    const url = `${this.apiBase}/updateTransportationBenefitModel`;
    return httpService.postJSON(url, tbm);
  }

  public deleteTransportationBenefitModel(
    tbm: TransportationBenefitModel,
  ): Observable<void> {
    const url = `${this.apiBase}/deleteTransportationBenefitModel`;
    return httpService.postJSON(url, {
      transportationBenefitModelUUID: tbm.transportationBenefitModelUUID,
    });
  }

  public activateTransportationBenefitModel(
    tbm: TransportationBenefitModel,
  ): Observable<TransportationBenefitModel> {
    const url = `${this.apiBase}/activateTransportationBenefitModel`;
    return httpService.postJSON(url, {
      transportationBenefitModelUUID: tbm.transportationBenefitModelUUID,
    });
  }

  public deactivateTransportationBenefitModel(
    tbm: TransportationBenefitModel,
  ): Observable<TransportationBenefitModel> {
    const url = `${this.apiBase}/deactivateTransportationBenefitModel`;
    return httpService.postJSON(url, {
      transportationBenefitModelUUID: tbm.transportationBenefitModelUUID,
    });
  }

  public getTransportationBenefitFunctions(
    states?: TransportationBenefitModelState[],
  ): Observable<TransportationBenefitFunction[]> {
    let url = `${this.apiBase}/getTransportationBenefitFunctions`;
    if (states) {
      url += states?.join(',');
    }
    return httpService.getJSON(url);
  }

  public getTransportationBenefits(
    clientBC: string,
  ): Observable<TransportationBenefit[]> {
    let url = `${this.apiBase}/getTransportationBenefits?clientBC=${clientBC}`;
    return httpService.getJSON(url);
  }

  public createTransportationBenefit(
    csp: TransportationBenefit,
  ): Observable<TransportationBenefit> {
    const url = `${this.apiBase}/createTransportationBenefit`;
    return httpService.postJSON(url, csp);
  }

  public updateTransportationBenefit(
    csp: TransportationBenefit,
  ): Observable<TransportationBenefit> {
    const url = `${this.apiBase}/updateTransportationBenefit`;
    return httpService.postJSON(url, csp);
  }

  public updateTransportationBenefitEffectiveDates(
    clientBC: string,
    transportationBenefitUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<TransportationBenefit> {
    const url = `${this.apiBase}/updateTransportationBenefitEffectiveDates`;
    return httpService.postJSON(url, {
      clientBC,
      transportationBenefitUUID,
      effectiveStartDate,
      effectiveEndDate,
    });
  }

  public getPassengerIngestionServerConfigurations(
    clientBC: string,
    states?: ClientState[],
  ): Observable<PassengerIngestionServerConfiguration[]> {
    let url = `${this.apiBase}/getPassengerIngestionServerConfigurations?clientBC=${clientBC}`;
    if (states?.length) {
      url += `&states=${states.join(',')}`;
    }
    return httpService.getJSON(url);
  }

  public createPassengerIngestionServerConfiguration(
    data: CreatePassengerIngestionServerConfigurationRequest,
  ): Observable<PassengerIngestionServerConfiguration> {
    const url = `${this.apiBase}/createPassengerIngestionServerConfiguration`;
    return httpService.postJSON(url, data);
  }

  public updatePassengerIngestionServerConfiguration(
    data: EditablePassengerIngestionServerConfiguration,
  ): Observable<PassengerIngestionServerConfiguration> {
    const url = `${this.apiBase}/updatePassengerIngestionServerConfiguration`;
    return httpService.postJSON(url, data);
  }

  public deletePassengerIngestionServerConfiguration(
    clientBC: string,
    passengerEligibilitySettingUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deletePassengerIngestionServerConfiguration`;
    return httpService.postJSON(url, {
      clientBC,
      passengerEligibilitySettingUUID,
    });
  }

  public updatePassengerIngestionServerConfigurationEffectiveDates(
    payload: UpdatePassengerIngestionServerConfigurationEffectiveDatesRequest,
  ): Observable<PassengerIngestionServerConfiguration> {
    const url = `${this.apiBase}/updatePassengerIngestionServerConfigurationEffectiveDates`;
    return httpService.postJSON(url, payload);
  }

  public createNamedLocation(location: CreatableNamedLocation) {
    const url = `${this.apiBase}/createNamedLocation`;
    return httpService.postJSON(url, location);
  }

  public updateNamedLocation(location: EditableNamedLocation) {
    const url = `${this.apiBase}/updateNamedLocation`;
    return httpService.postJSON(url, location);
  }

  public deleteNamedLocation(namedLocationUUID: string) {
    const url = `${this.apiBase}/deleteNamedLocation`;
    return httpService.postJSON(url, { namedLocationUUID });
  }

  public getNamedLocationsByUUID(namedLocationUUIDs: string[]) {
    const url = `${this.apiBase}/getNamedLocationsByUUID?namedLocationUUIDs=${namedLocationUUIDs.join(',')}`;
    return httpService.getJSON(url);
  }

  public getClientLocations(clientBC: string) {
    const url = `${this.apiBase}/getClientLocations?clientBC=${clientBC}`;
    return httpService.getJSON(url);
  }

  public createClientLocation(clientLocation: CreatableClientLocation) {
    const url = `${this.apiBase}/createClientLocation`;
    return httpService.postJSON(url, clientLocation);
  }

  public deleteClientLocation(payload: DeleteClientLocationRequest) {
    const url = `${this.apiBase}/deleteClientLocation`;
    return httpService.postJSON(url, payload);
  }

  public getClientSettings(clientBC: string): Observable<ClientSetting[]> {
    const url = `${this.apiBaseTransport}/${clientBC}/clientSettings`;
    return httpService.getJSON(url);
  }
  public saveClientSettings(
    clientBC: string,
    prefs: ClientSetting[],
  ): Observable<ClientSetting[]> {
    const url = `${this.apiBaseTransport}/${clientBC}/clientSettings`;
    return httpService.putJSON(url, prefs);
  }

  public getPassengerIngestionExecutions(
    clientBC: string,
    status?: PassengerIngestionExecutionStatus,
  ) {
    // TODO: Call API to fetch executions
    return of([]);
  }

  public getClientAdminSettings(
    clientBC: string,
  ): Observable<ClientAdminSettings> {
    const url = `${this.apiBase}/getClientAdministrationSettings?clientBC=${clientBC}`;
    return httpService.getJSON(url);
  }

  public updateClientAdminSettings(
    settings: ClientAdminSettings,
  ): Observable<ClientAdminSettings> {
    const url = `${this.apiBase}/updateClientAdministrationSettings`;
    return httpService.postJSON(url, settings);
  }
}

const clientService = new ClientService();

export default clientService;
